import type { DocumentStatusV2Schema } from '@/client';
import {
	getDocumentReadinessThresholds,
	getKnowledgeBaseReadinessThresholds,
	hasKnowledgeBasePermanentFailure,
	hasPermanentFailure,
	isDocumentProcessingComplete,
	isRunCompleted,
} from '@/utils/pipelineStatusUtils';

export const KnowledgeBaseReadinessState = {
	Processing: 'processing',
	Delayed: 'delayed',
	Failed: 'failed',
	Mixed: 'mixed',
	Stalled: 'stalled',
} as const;

export type KnowledgeBaseReadinessState =
	(typeof KnowledgeBaseReadinessState)[keyof typeof KnowledgeBaseReadinessState];

export interface KnowledgeBaseReadinessSummary {
	state: KnowledgeBaseReadinessState | null;
	total: number;
	ready: number;
	reading: number;
	delayed: number;
	stalled: number;
	failed: number;
	/** Documents currently processing, stalled, or failed; used to scope transient dismissals. */
	attentionDocumentIds: number[];
}

export type DocumentReadinessSummary = KnowledgeBaseReadinessSummary;

interface KnowledgeBaseDocumentReference {
	id: number;
	uploaded_at?: string | null;
}

interface KnowledgeBaseReadinessCopy {
	advisory: string;
	advisoryMobile: string;
	bannerTitle: string;
	bannerMobileTitle: string;
	bannerDetail: string;
	bannerMobileDetail: string;
}

/**
 * Shared product language for KB readiness. Delayed remains a processing condition:
 * it changes the expectation-setting copy, not the interaction or severity model.
 */
export const KNOWLEDGE_BASE_READINESS_COPY: Record<KnowledgeBaseReadinessState, KnowledgeBaseReadinessCopy> = {
	[KnowledgeBaseReadinessState.Processing]: {
		advisory: "Some records are still processing. Answers won't include them yet.",
		advisoryMobile: "Some records are still processing. Answers won't include them yet.",
		bannerTitle: 'Bundle is reading through your property information and organising.',
		bannerMobileTitle: 'Bundle is reading and organising your records.',
		bannerDetail:
			"This keeps running in the background, so you don't need to wait here - it may take a little while the first time.",
		bannerMobileDetail: "This continues in the background, so you don't need to wait.",
	},
	[KnowledgeBaseReadinessState.Delayed]: {
		advisory: "Some records are taking longer to process. Answers won't include them yet.",
		advisoryMobile: "Some records are taking longer to process. Answers won't include them yet.",
		bannerTitle: 'Bundle is still getting your records ready.',
		bannerMobileTitle: 'Still getting your records ready.',
		bannerDetail:
			"There's nothing you need to do - you can keep using Bundle or leave this page while processing continues in the background.",
		bannerMobileDetail: 'No action needed - processing will continue in the background.',
	},
	[KnowledgeBaseReadinessState.Failed]: {
		advisory: "Some records couldn't be processed. Our team will review them - no action required.",
		advisoryMobile: "Some records couldn't be processed. Our team will review them - no action required.",
		bannerTitle: 'Some records could not be processed.',
		bannerMobileTitle: 'Some records could not be processed.',
		bannerDetail: 'Our team will review them - no action required.',
		bannerMobileDetail: 'Our team will review them - no action required.',
	},
	[KnowledgeBaseReadinessState.Mixed]: {
		advisory: "Some records are still processing; others couldn't be processed and are flagged for review.",
		advisoryMobile: 'Some records are processing; others are flagged for review.',
		bannerTitle: 'Bundle is still processing some records.',
		bannerMobileTitle: 'Some records are still processing.',
		bannerDetail:
			"Other records couldn't be processed and are flagged for our team to review - no action required.",
		bannerMobileDetail: 'Others are flagged for review - no action required.',
	},
	[KnowledgeBaseReadinessState.Stalled]: {
		advisory: 'Processing has stalled for some new files. Please delete and re-upload them.',
		advisoryMobile: 'Processing has stalled for some new files. Please delete and re-upload them.',
		bannerTitle: 'Processing has stalled for some files.',
		bannerMobileTitle: 'Processing has stalled for some files.',
		bannerDetail: 'Please retry these files by deleting and re-uploading them.',
		bannerMobileDetail: 'Please retry these files by deleting and re-uploading them.',
	},
};

export const getAskReadinessCopy = (
	state: KnowledgeBaseReadinessState,
	stalledFileCount?: number,
): { advisory: string; advisoryMobile: string } => {
	if (!stalledFileCount) {
		const { advisory, advisoryMobile } = KNOWLEDGE_BASE_READINESS_COPY[state];
		return { advisory, advisoryMobile };
	}

	const stalledCopy =
		stalledFileCount === 1
			? 'Processing has stalled for a new file. Please delete and re-upload it.'
			: 'Processing has stalled for some new files. Please delete and re-upload them.';
	const advisory =
		state === KnowledgeBaseReadinessState.Mixed
			? `${stalledCopy} Other files are flagged for review.`
			: stalledCopy;
	return { advisory, advisoryMobile: advisory };
};

export const hasKnowledgeBaseFailure = (state: KnowledgeBaseReadinessState): boolean =>
	state === KnowledgeBaseReadinessState.Failed || state === KnowledgeBaseReadinessState.Mixed;

export const isKnowledgeBaseActivelyProcessing = (summary: KnowledgeBaseReadinessSummary | null): boolean =>
	(summary?.reading ?? 0) > 0;

/** Records only needs an aggregate banner when it adds information beyond a single processing row. */
export const shouldShowRecordsKnowledgeBaseBanner = (summary: KnowledgeBaseReadinessSummary | null): boolean =>
	summary !== null && summary.reading + summary.stalled + summary.failed > 1;

export const deriveKnowledgeBaseReadinessState = (
	documents: DocumentStatusV2Schema[],
	now = Date.now(),
): KnowledgeBaseReadinessState | null =>
	deriveKnowledgeBaseReadinessSummary(
		documents,
		Object.fromEntries(documents.map((document) => [document.id, document])),
		now,
	).state;

type KnowledgeBaseReadinessCounts = Pick<
	KnowledgeBaseReadinessSummary,
	'ready' | 'reading' | 'delayed' | 'stalled' | 'failed'
>;
type ReadinessProjection = 'knowledge-base' | 'document';

const countReadiness = (
	counts: KnowledgeBaseReadinessCounts,
	attentionDocumentIds: number[],
	document: KnowledgeBaseDocumentReference,
	status: DocumentStatusV2Schema | undefined,
	now: number,
	projection: ReadinessProjection,
) => {
	const failed = projection === 'document' ? hasPermanentFailure(status) : hasKnowledgeBasePermanentFailure(status);
	if (failed) {
		counts.failed += 1;
		attentionDocumentIds.push(document.id);
		return;
	}
	const complete = projection === 'document' ? isDocumentProcessingComplete(status) : isRunCompleted(status?.kbs);
	if (complete) {
		counts.ready += 1;
		return;
	}
	const { stalledAt, delayedAt } =
		projection === 'document'
			? getDocumentReadinessThresholds(status, document.uploaded_at, now)
			: getKnowledgeBaseReadinessThresholds(status, document.uploaded_at, now);
	if (stalledAt !== undefined && now >= stalledAt) {
		counts.stalled += 1;
		attentionDocumentIds.push(document.id);
		return;
	}

	counts.reading += 1;
	attentionDocumentIds.push(document.id);
	if (delayedAt !== undefined && now >= delayedAt) counts.delayed += 1;
};

const createKnowledgeBaseReadinessSummary = (
	counts: KnowledgeBaseReadinessCounts,
	total: number,
	attentionDocumentIds: number[],
): KnowledgeBaseReadinessSummary => {
	const { ready, reading, delayed, stalled, failed } = counts;
	const state =
		failed > 0 && (reading > 0 || stalled > 0)
			? KnowledgeBaseReadinessState.Mixed
			: failed > 0
				? KnowledgeBaseReadinessState.Failed
				: stalled > 0
					? KnowledgeBaseReadinessState.Stalled
					: delayed > 0
						? KnowledgeBaseReadinessState.Delayed
						: reading > 0
							? KnowledgeBaseReadinessState.Processing
							: null;

	return { state, total, ready, reading, delayed, stalled, failed, attentionDocumentIds };
};

export const deriveKnowledgeBaseReadinessSummary = (
	documents: KnowledgeBaseDocumentReference[],
	statusById: Record<number, DocumentStatusV2Schema>,
	now = Date.now(),
): KnowledgeBaseReadinessSummary => {
	const counts: KnowledgeBaseReadinessCounts = { ready: 0, reading: 0, delayed: 0, stalled: 0, failed: 0 };
	const attentionDocumentIds: number[] = [];

	for (const document of documents) {
		countReadiness(counts, attentionDocumentIds, document, statusById[document.id], now, 'knowledge-base');
	}

	return createKnowledgeBaseReadinessSummary(counts, documents.length, attentionDocumentIds);
};

/** Derives readiness directly from the authoritative house-status response. */
export const deriveKnowledgeBaseReadinessSummaryFromStatuses = (
	statusById: Record<number, DocumentStatusV2Schema>,
	now = Date.now(),
): KnowledgeBaseReadinessSummary => {
	const counts: KnowledgeBaseReadinessCounts = { ready: 0, reading: 0, delayed: 0, stalled: 0, failed: 0 };
	const attentionDocumentIds: number[] = [];
	let total = 0;
	for (const id in statusById) {
		const status = statusById[id];
		countReadiness(counts, attentionDocumentIds, status, status, now, 'knowledge-base');
		total += 1;
	}
	return createKnowledgeBaseReadinessSummary(counts, total, attentionDocumentIds);
};

/** Overall processing projection used by Records; includes enrichment as well as KB indexing. */
export const deriveDocumentReadinessSummaryFromStatuses = (
	statusById: Record<number, DocumentStatusV2Schema>,
	now = Date.now(),
): DocumentReadinessSummary => {
	const counts: KnowledgeBaseReadinessCounts = { ready: 0, reading: 0, delayed: 0, stalled: 0, failed: 0 };
	const attentionDocumentIds: number[] = [];
	let total = 0;
	for (const id in statusById) {
		const status = statusById[id];
		countReadiness(counts, attentionDocumentIds, status, status, now, 'document');
		total += 1;
	}
	return createKnowledgeBaseReadinessSummary(counts, total, attentionDocumentIds);
};
